import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { postCommunityFeedReportApi } from '@flipster/apis/v1/community';
import { CommunityFeedItemAbuseType } from '@flipster/apis/v1/community/types';
import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { stack } from '@flipster/styled-system/patterns';
import { getErrorMessage } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';

const ReportReasons: { kind: CommunityFeedItemAbuseType; label: string }[] = [
  { kind: 'SPAM', label: 'Spam or unwanted ads' },
  { kind: 'SCAM_OR_FRAUD', label: 'Scam or fraud' },
  { kind: 'HATE_SPEECH_OR_HARASSMENT', label: 'Hate speech or harassment' },
  { kind: 'INAPPROPRIATE_CONTENT', label: 'Explicit or inappropriate content' },
  { kind: 'PERSONAL_INFORMATION_LEAK', label: 'Personal information leak' },
  { kind: 'FALSE_OR_MISLEADING_INFORMATION', label: 'False or misleading information' },
  { kind: 'OFF_TOPIC_OR_IRRELEVANT', label: 'Off-topic or irrelevant' },
];

interface ReportPostModalProps {
  feedId: string;
  symbol: string;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}
export const ReportPostModal = ({ feedId, symbol, isOpen, onClose, onExit }: ReportPostModalProps) => {
  const t = useTranslations();
  const [selectedReason, setSelectedReason] = useState<CommunityFeedItemAbuseType | null>();
  return (
    <Modal isOpen={isOpen} onExit={onExit}>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title label={t('Autogen_common.r251126.s_Report_post')} />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 8 })}>
          <p>
            <FormattedMessage id='Autogen_common.r251126.s_Select_a_reason_for_reporting_this_post' />
          </p>
          <div className={stack()}>
            {ReportReasons.map(({ kind, label }) => {
              return (
                <RadioButton name='reason' key={kind} onClick={() => setSelectedReason(kind)} css={{ py: 12 }}>
                  {label}
                </RadioButton>
              );
            })}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button hierarchy='secondary' color='neutral' label={t('Common.r250401.Cancel')} onClick={onClose} />
        <Button
          label={t('Autogen_common.r251126.s_Report_post')}
          withSpinner
          disabled={selectedReason == null}
          onClick={async () => {
            if (!selectedReason) return;
            segment.track(CommunityEvent.ReportPostClick, {
              feed_id: feedId,
              report_type: selectedReason,
            });
            try {
              await postCommunityFeedReportApi(symbol, feedId, { abuseType: selectedReason });
              snackbar.success({ message: 'Your report has been received' });
            } catch (e) {
              snackbar.error({ message: getErrorMessage(e) });
            } finally {
              onClose();
            }
          }}
        />
      </Modal.Footer>
    </Modal>
  );
};
